import { useEffect, useState } from 'react';

import { dateKeyOf } from '../../../shared/time/date-key.js';

/**
 * Today's date key in the application timezone, rolling over at Eastern midnight.
 *
 * The old client set the day once on load and never updated it, so a tab left
 * open past midnight Eastern kept showing yesterday while background effects had
 * already started writing under the new day's key.
 *
 * The key is compared before it is set, so a poll that finds no change does not
 * re-render anything.
 *
 * Audit reference prevented here: App.jsx #5.
 *
 * @param {number} checkIntervalMs  How often to test for a rollover.
 * @returns {string} A `YYYY-MM-DD` Eastern calendar date.
 * @throws {RangeError} if the interval is not a positive finite number.
 */
export function useAppDateKey(checkIntervalMs) {
  if (!Number.isFinite(checkIntervalMs) || checkIntervalMs <= 0) {
    throw new RangeError(`useAppDateKey needs a positive interval, received ${checkIntervalMs}`);
  }

  const [dateKey, setDateKey] = useState(() => dateKeyOf(new Date()));

  useEffect(() => {
    const timer = setInterval(() => {
      const next = dateKeyOf(new Date());
      setDateKey((current) => (current === next ? current : next));
    }, checkIntervalMs);
    return () => clearInterval(timer);
  }, [checkIntervalMs]);

  return dateKey;
}
